/**
 * Cliente Flex Microform v2 (BanBajío/CyberSource) · Sprint 59 · F1A.
 *
 * SOLO obtiene el contexto de captura y genera el token transitorio.
 * CT Connect NUNCA recibe ni almacena PAN/CVV: se capturan dentro de los
 * iframes alojados de CyberSource. El token transitorio NUNCA se guarda en
 * URL, localStorage ni sessionStorage; vive solo en memoria del componente.
 * No implementa 3DS ni autorización/captura (eso es F1B/F2).
 */

import { api } from "@/services/api";

export const PAYMENT_PROVIDER = (
  process.env.REACT_APP_PAYMENT_PROVIDER || "mock"
).toLowerCase();

export const esBanbajio = () => PAYMENT_PROVIDER === "banbajio";

// Idempotencia REAL de capture-context por compraId a nivel de módulo (store).
// Inmune al ciclo montaje/desmontaje/remontaje de React StrictMode y a efectos
// con callbacks de identidad variable: colapsa llamadas concurrentes o casi
// simultáneas en UNA sola petición de red. NO reutiliza un contexto vencido:
// la entrada exitosa se libera tras una ventana breve (< vigencia real ~15 min)
// y los errores se liberan de inmediato para permitir reintento manual.
const _ctxEnCurso = new Map(); // compraId -> Promise<data>
const _CTX_RETENCION_MS = 8000;

/** Pide al backend el contexto de captura de una compra pendiente válida. */
export const crearCaptureContext = async (compraId) => {
  if (!compraId) return { error: "sin_compra", status: 0 };
  if (_ctxEnCurso.has(compraId)) return _ctxEnCurso.get(compraId);

  const p = (async () => {
    const { data } = await api.post("/pagos/capture-context", { compraId });
    return data; // { captureContext, vigenciaContextoSegundos, ... }
  })();
  _ctxEnCurso.set(compraId, p);

  try {
    const data = await p;
    // Retención breve: colapsa el doble montaje de StrictMode en 1 sola llamada.
    setTimeout(() => {
      if (_ctxEnCurso.get(compraId) === p) _ctxEnCurso.delete(compraId);
    }, _CTX_RETENCION_MS);
    return data;
  } catch (err) {
    _ctxEnCurso.delete(compraId); // libera de inmediato → permite reintento
    return {
      error: err?.response?.data?.detail || "network_error",
      status: err?.response?.status || 0,
    };
  }
};

/** Solo pruebas: limpia la caché de idempotencia de capture-context. */
export const _resetCaptureContextCache = () => _ctxEnCurso.clear();

/** Decodifica el payload del capture context JWT (sin verificar firma). */
export const decodificarCaptureContext = (jwt) => {
  const parte = String(jwt || "").split(".")[1];
  if (!parte) throw new Error("capture context inválido");
  const json = atob(parte.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json);
};

/**
 * Extrae clientLibrary y clientLibraryIntegrity del contexto decodificado.
 * El manual exige extraerlos por transacción; nunca codificarlos fijos.
 */
export const extraerFlexLib = (jwt) => {
  const payload = decodificarCaptureContext(jwt);
  const data = payload?.ctx?.[0]?.data || {};
  const clientLibrary = data.clientLibrary;
  const clientLibraryIntegrity = data.clientLibraryIntegrity;
  if (!clientLibrary || !clientLibraryIntegrity) {
    throw new Error("El contexto de captura no incluye la biblioteca Flex.");
  }
  return { clientLibrary, clientLibraryIntegrity };
};

/**
 * Extrae el `jti` del token transitorio Flex. IMPORTANTE (retractación): el jti
 * es un identificador TEMPORAL de las credenciales de pago capturadas y puede
 * usarse en solicitudes de servicio, por lo que se trata como material sensible.
 * NUNCA debe persistirse en claro ni enviarse a storage/URL/logs. Uso interno.
 */
export const extraerJti = (transientToken) => {
  try {
    const parte = String(transientToken || "").split(".")[1];
    if (!parte) return null;
    const json = atob(parte.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json)?.jti || null;
  } catch {
    return null;
  }
};
